import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Skeleton } from '@art-of-hospitality-solutions-llc/ui-kit/components/skeleton';
import Breadcrumbs from '@Components/Breadcrumbs';
import { useVenues } from '@Hooks/useVenues';
import type { Venue } from '@Types/venue';
import { useMemo } from 'react';
import EventBookingMatrix, { type EBMatrixRow } from './EventBookingMatrix';
import TicketedEventsMatrix from './TicketedEventsMatrix';
import { evaluateEventBookingForSpace } from './features/eventBooking';

export default function FeatureMatrix() {
  const { data, status, error } = useVenues({
    pageSize: -1,
    include: 'spaces',
  });

  const visibleVenues = useMemo<Venue[]>(() => {
    const venues: Venue[] = Array.isArray(data) ? data : [];
    return venues
      .filter((v) => v.deletedAt === null)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const eventBookingRows = useMemo<EBMatrixRow[]>(() => {
    const result: EBMatrixRow[] = [];
    for (const venue of visibleVenues) {
      const spaces = (venue.spaces ?? [])
        .filter((s) => s.deletedAt === null)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name));

      const onCount = spaces.reduce(
        (acc, space) =>
          acc +
          (evaluateEventBookingForSpace(venue, space).eventBookingOn ? 1 : 0),
        0,
      );

      result.push({
        kind: 'venue',
        venue,
        spaceCount: spaces.length,
        onCount,
      });
      for (const space of spaces) {
        result.push({ kind: 'space', venue, space });
      }
    }
    return result;
  }, [visibleVenues]);

  const isLoading = status === 'pending';
  const isError = status === 'error';
  const isReady = status === 'success';

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Locations', href: '/locations' },
          { name: 'Feature Matrix', href: '/feature-matrix' },
        ]}
      />
      <div className="mt-8 mb-4">
        <Heading>Feature Matrix</Heading>
        <p>Feature readiness across every venue and space.</p>
      </div>

      {isError && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Failed to load venues</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : 'Please try again.'}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <Card>
          <CardContent>
            <h2 className="mb-3 text-lg font-semibold text-zinc-900">
              Event Booking
            </h2>
            {isLoading && <MatrixSkeleton />}
            {isReady && <EventBookingMatrix rows={eventBookingRows} />}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <h2 className="mb-3 text-lg font-semibold text-zinc-900">
              Ticketed Events
            </h2>
            {isLoading && <MatrixSkeleton />}
            {isReady && <TicketedEventsMatrix venues={visibleVenues} />}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function MatrixSkeleton() {
  return (
    <div className="space-y-2 py-4">
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
    </div>
  );
}
